/**
 * Feedback on the assistant (👍/👎). A 👍 or a 👎 alone sends only the rating and an optional
 * comment. When the user ticks "share" (on the 👎 form, or after a 👍), the conversation, the
 * context the model saw, its proposal and the project are sent too, so the request can be
 * replayed as a test case, and with the second box ("train") also used to train Tablo's own
 * model (`consent`). The project first loses what identifies people (owner, addresses, author, company, project title,
 * survey notes), its photos, revisions, measurements, inspection reports and invoices.
 */
import type { Project } from '@elec/domain';
import type { AssistAction, AssistMessage } from './assist.ts';

/** What a shared answer may be used for: a test case, or also training Tablo's own model. */
export type FeedbackConsent = 'test' | 'test+train';

export type SharedAnswer = NonNullable<AssistFeedback['shared']>;

export interface AssistFeedback {
  rating: 1 | -1;
  comment: string;
  locale: 'fr' | 'en' | 'nl';
  /** App version and platform, e.g. "0.12.0 ios". The server adds the model and its release. */
  app?: string;
  shared?: {
    messages: AssistMessage[];
    context: string;
    reply: string;
    actions: AssistAction[];
    summary: string[];
    skipped: string[];
    findings: string[];
    project: Project;
  };
  /** Required with `shared`. */
  consent?: FeedbackConsent;
}

/** The project without personal data or attachments, for a shared feedback. */
export function redactProject(project: Project): Project {
  const p = structuredClone(project);
  p.meta = {
    ...p.meta,
    title: '',
    ownerName: '',
    ownerAddress: '',
    installationAddress: '',
    author: '',
    company: '',
  };
  p.revisions = {};
  p.measurements = {};
  p.inspections = {};
  p.remarks = {};
  p.invoices = {};
  for (const s of Object.values(p.sheets)) delete s.background;
  for (const e of Object.values(p.elements)) delete e.photos;
  for (const r of Object.values(p.surveyRooms)) {
    r.notes = '';
    r.photos = [];
  }
  for (const x of Object.values(p.surveyPoints)) {
    x.notes = '';
    delete x.photos;
  }
  for (const x of Object.values(p.surveyBoards)) x.photos = [];
  return p;
}
